import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import orderBy from 'lodash/orderBy';
import { Box, Stack, Button } from '@mui/material';
import TicketCategory from './TicketCategory';
import TicketTypes from './TicketTypes';
import TicketCountSelect from './TicketCountSelect';
import useTicketsContext from '@/contexts/tickets/useTicketsContext';
import useEventContext from '@/contexts/event/useEventContext';
import { extractData } from '@/store/ticket-types/useTicketTypes';
import useMobile from '@/hooks/useMobile';
import PromotionButton from '@/sections/promotion/PromotionButton';
import PromotionBanner from '@/sections/promotion/PromotionBanner';
import { useTicketsSelected, useSummary, useEvent } from '@/store';
import Iconify from '@/components/Iconify';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';
import MsciBanner from '@/sections/msi/MsciBanner';
import useMerch from '@/hooks/useMerch';
import useMsciBanner from '@/hooks/useMsciBanner';
import useTracking from '@/hooks/userTracking';
import RenderIf from '@/components/RenderIf';

function TicketsSelector({ onTicketCategorySelected }) {
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const { onTicketSelected, onTicketTypeSelected, clearSelection } =
    useTicketsContext();
  const [sort, setSort] = useState();
  const [show, setShow] = useState(false);
  const { hasTickets } = useTicketsSelected();
  const { orderByPrice, clearTickets } = useAnalyticsEvents();
  const {
    // ticketSelectionViewed,  temperally disabled
    // mapZoneSelected, temperally disabled
    listTicketQuantityChanged,
  } = useTracking();

  const { event } = useEvent();

  const { bannerKind: paymentConfig } = useMsciBanner();
  const {
    event: {
      event: { max_tickets_per_booking, use_seats, id },
    },
    ticketTypes: ticketsData,
  } = useEventContext();

  // TODO create automatically function that takes coupon and apply it to tickets deafult selected number
  const [numberOfTicketsSelected, setNumberOfTicketsSelected] = useState(
    id === 258792 ? 3 : 2,
  );
  const { eventHasMerch, eventHasMerchToShipping } = useMerch();

  const { ticketTypes } = extractData(ticketsData);

  const isWithMerch = eventHasMerchToShipping(id) || eventHasMerch(id);

  const onSortHandler = () => {
    if (sort) {
      setSort((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort('desc');
    }
    orderByPrice();
  };

  const onTicketSelectedHandler = async (ticket) => {
    const resp = await onTicketSelected({
      ticket,
      quantity: numberOfTicketsSelected,
    });
    if (resp) {
      onTicketCategorySelected(ticket);
      /*
       * event disable temporaly
       * mapZoneSelected({
       *   zoneId: ticket?.category_id,
       *   zoneName: ticket?.category_name,
       *   quantity: numberOfTicketsSelected,
       * });
       */
    }
  };

  const onTicketTypeQuantityChangeHandler = (ticket, bookingQuantity) => {
    onTicketTypeSelected({
      ticket,
      quantity: bookingQuantity,
    });
    listTicketQuantityChanged({
      zoneId: ticket?.id,
      zoneName: ticket?.name,
      quantity: bookingQuantity,
    });
  };

  const getOrderedTicket = () => {
    const orderByAvailability = isWithMerch
      ? orderBy(ticketTypes, ['id', 'availability'], ['asc', 'desc'])
      : orderBy(ticketTypes, ['availability'], ['desc']);

    if (sort) {
      return orderBy(orderByAvailability, ['max_price'], [sort]);
    }
    return orderByAvailability;
  };

  const clearTicketsHandler = async () => {
    await clearSelection();
    clearTickets();
  };

  useEffect(() => {
    setShow(true);
  }, []);

  return (
    <Box>
      <RenderIf condition={isMobile && event?.preferences?.currency === 'MXN'}>
        <MsciBanner />
      </RenderIf>
      <Stack
        spacing={isMobile ? 2 : 4}
        justifyContent="space-between"
        direction="row"
        sx={{
          mt: 2,
          mb: 2,
        }}
      >
        {show && use_seats && (
          <>
            {!hasTickets && (
              <TicketCountSelect
                max={max_tickets_per_booking}
                value={numberOfTicketsSelected}
                onChange={(value) => {
                  setNumberOfTicketsSelected(value);
                }}
              />
            )}
            {hasTickets && (
              <Button
                variant="outlined"
                startIcon={
                  <Iconify
                    width={14}
                    height={14}
                    icon={'mdi:ticket-confirmation-outline'}
                  />
                }
                sx={{
                  padding: '14px',
                  borderRadius: '24px',
                }}
                onClick={clearTicketsHandler}
              >
                {t('checkout.buttons.clearTickets')}
              </Button>
            )}
          </>
        )}
        {show && (
          <>
            <Button
              type="submit"
              variant="text"
              onClick={onSortHandler}
              margin={0}
              padding={0}
              startIcon={
                <Iconify width={14} height={14} icon={'mdi:swap-vertical'} />
              }
              sx={{
                'fontWeight': 500,
                'fontSize': '14px',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.blue[50],
                },
                'backgroundColor':
                  sort === 'desc' && !isMobile
                    ? (theme) => theme.palette.backgroundBlue
                    : '',
                ...(isMobile
                  ? {
                      pl: 1,
                      pr: 0,
                    }
                  : {}),
              }}
            >
              {t('checkout.buttons.orderByPrice')}
            </Button>
          </>
        )}
        {show && !use_seats && <PromotionButton rounded />}
      </Stack>
      {show && !use_seats && (
        <Box
          sx={{
            width: '100%',
            mt: 2,
          }}
        >
          <PromotionBanner />
        </Box>
      )}
      {use_seats &&
        getOrderedTicket().map((item) => {
          return (
            <TicketCategory
              key={item.ticket_type_id}
              ticket={item}
              onTicketSelected={onTicketSelectedHandler}
            />
          );
        })}
      {!use_seats &&
        getOrderedTicket().map((item) => {
          return (
            <TicketTypes
              key={item.ticket_type_id}
              ticket={item}
              onQuantityChange={onTicketTypeQuantityChangeHandler}
            />
          );
        })}
    </Box>
  );
}

export default TicketsSelector;
